
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';
import { syncProductsWithApi } from '@/services/productApiService';
import { syncCategoriesWithApi } from '@/services/categoryApiService';
import { syncUsersWithApi } from '@/services/userApiService';
import { syncPaymentMethods } from '@/services/paymentMethodApiService';
import { getApiToken } from '@/services/authService';
import { useApp } from '@/contexts/AppContext';
import { apiService } from '@/services/apiService';
import { API_CONFIG } from '@/config/apiConfig';
import { Loader2, Info } from 'lucide-react';

const ApiSettings: React.FC = () => {
  const { refreshData } = useApp();
  const [apiUrl, setApiUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [savedToken, setSavedToken] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [autoSync, setAutoSync] = useState(false);
  const [syncInterval, setSyncInterval] = useState('60');
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    const savedApiUrl = localStorage.getItem('api_base_url') || '';
    const savedApiKey = localStorage.getItem('api_key') || '';
    const savedAutoSync = localStorage.getItem('auto_sync') || 'false';
    const savedSyncInterval = localStorage.getItem('sync_interval') || '60';
    const token = getApiToken();
    
    setApiUrl(savedApiUrl);
    setApiKey(savedApiKey);
    setSavedToken(token);
    setAutoSync(savedAutoSync === 'true');
    setSyncInterval(savedSyncInterval);
    
    API_CONFIG.initialize();
  }, []);

  useEffect(() => {
    let intervalId: number | undefined;
    
    if (autoSync && apiUrl) {
      const minutes = parseInt(syncInterval, 10);
      if (!isNaN(minutes) && minutes > 0) {
        const milliseconds = minutes * 60 * 1000;
        
        intervalId = window.setInterval(() => {
          console.log('Executando sincronização automática...');
          handleSyncData(true);
        }, milliseconds);
      }
    }
    
    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [autoSync, syncInterval, apiUrl]);

  const handleSaveSettings = async () => {
    try {
      await apiService.updateConfig(apiUrl, apiKey);
      
      localStorage.setItem('auto_sync', autoSync.toString());
      localStorage.setItem('sync_interval', syncInterval);
      
      toast({
        title: 'Configurações salvas',
        description: 'As configurações da API foram salvas com sucesso',
      });
      
      API_CONFIG.initialize();
      
      console.log('Configurações da API atualizadas:', {
        apiUrl,
        hasApiKey: !!apiKey,
        autoSync,
        syncInterval,
        apiConfigBaseUrl: API_CONFIG.baseURL
      });
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast({
        title: 'Erro ao salvar',
        description: 'Ocorreu um erro ao salvar as configurações',
        variant: 'destructive',
      });
    }
  };

  const handleSyncData = async (silent = false) => {
    if (!apiUrl) {
      if (!silent) {
        toast({
          title: 'URL da API não configurada',
          description: 'Por favor, configure a URL da API antes de sincronizar',
          variant: 'destructive',
        });
      }
      return;
    }

    if (!silent) setIsSyncing(true);
    let syncSuccess = false;
    
    try {
      console.log('Iniciando sincronização de dados...');
      
      await apiService.updateConfig(apiUrl, apiKey);
      API_CONFIG.initialize();
      
      console.log('Configuração da API atualizada:', {
        baseURL: API_CONFIG.baseURL,
        hasApiKey: !!API_CONFIG.apiKey,
        useCorsProxy: API_CONFIG.useCorsProxy
      });
      
      console.log('Sincronizando categorias...');
      const categoriesResult = await syncCategoriesWithApi();
      console.log('Resultado da sincronização de categorias:', categoriesResult);
      
      console.log('Sincronizando produtos...');
      const productsResult = await syncProductsWithApi();
      console.log('Resultado da sincronização de produtos:', productsResult);
      
      console.log('Sincronizando usuários...');
      const usersResult = await syncUsersWithApi();
      console.log('Resultado da sincronização de usuários:', usersResult);
      
      console.log('Sincronizando formas de pagamento...');
      const paymentResult = await syncPaymentMethods(true);
      console.log('Resultado da sincronização de formas de pagamento:', paymentResult);
      
      refreshData();
      syncSuccess = productsResult || categoriesResult || usersResult || paymentResult;
      
      if (!silent && syncSuccess) {
        toast({
          title: 'Sincronização concluída',
          description: `Dados sincronizados com sucesso (${categoriesResult ? 'categorias, ' : ''}${productsResult ? 'produtos, ' : ''}${usersResult ? 'usuários, ' : ''}${paymentResult ? 'formas de pagamento' : ''})`,
        });
      } else if (!silent && !syncSuccess) {
        toast({
          title: 'Sem dados para sincronizar',
          description: 'Não foram encontrados novos dados para sincronizar',
          variant: 'default',
        });
      }
    } catch (error) {
      console.error('Erro na sincronização:', error);
      if (!silent) {
        toast({
          title: 'Erro na sincronização',
          description: 'Ocorreu um erro ao sincronizar os dados',
          variant: 'destructive',
        });
      }
    } finally {
      if (!silent) setIsSyncing(false);
    }
  };

  const handleTestConnection = async () => {
    if (!apiUrl) {
      toast({
        title: 'URL da API não configurada',
        description: 'Por favor, configure a URL da API antes de testar',
        variant: 'destructive',
      });
      return;
    }

    setIsTesting(true);
    
    try {
      // Validate URL format first
      try {
        new URL(apiUrl);
      } catch (urlError) {
        toast({
          title: 'URL inválida',
          description: 'O formato da URL não é válido. Use formato https://exemplo.com',
          variant: 'destructive',
        });
        setIsTesting(false);
        return;
      }
      
      await apiService.updateConfig(apiUrl, apiKey);
      API_CONFIG.initialize();
      
      const endpoint = API_CONFIG.endpoints.paymentMethods.list; // Use more reliable endpoint
      
      const fullUrl = `${apiUrl}${endpoint}`;
      console.log(`[Teste] Testando conexão com: ${fullUrl}`);
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      
      const token = apiKey || 
                    localStorage.getItem('api_auth_token') || 
                    localStorage.getItem('api_key') || '';
      
      if (token) {
        headers['HeaderToken'] = token;
        console.log('[Teste] Usando token para autenticação');
      } else {
        console.warn('[Teste] Nenhum token disponível para autenticação');
      }
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      console.log(`[Teste] Status da resposta: ${response.status} ${response.statusText}`);
      
      if (response.ok) {
        // Check content type first
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          console.log(`[Teste] Dados recebidos: ${Array.isArray(data) ? `Array com ${data.length} itens` : 'Não é um array'}`);
          
          toast({
            title: 'Conexão estabelecida',
            description: `A conexão com a API foi estabelecida com sucesso (${Array.isArray(data) ? `${data.length} itens recebidos` : 'resposta recebida'})`,
          });
        } else {
          // Handle HTML or other non-JSON responses
          const text = await response.text();
          if (text.includes('<!DOCTYPE') || text.includes('<html')) {
            toast({
              title: 'Recebeu página HTML ao invés de JSON',
              description: 'A API pode estar retornando uma página de login ou erro. Verifique a URL e autenticação.',
              variant: 'destructive',
            });
          } else {
            toast({
              title: 'Resposta recebida em formato não-JSON',
              description: `Tipo de conteúdo: ${contentType || 'desconhecido'}`,
              variant: 'destructive',
            });
          }
        }
      } else {
        toast({
          title: 'Erro na resposta da API',
          description: `Código: ${response.status} - ${response.statusText}`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Erro ao testar conexão:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Não foi possível conectar à API';
      
      // Handle specific error messages
      if (errorMessage.includes('pattern')) {
        toast({
          title: 'URL inválida',
          description: 'O formato da URL não é válido. Use formato https://exemplo.com',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Erro de conexão',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Configurações da API</CardTitle>
        <CardDescription>Configure a conexão com o ERP</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="api-url">URL da API</Label>
          <Input
            id="api-url"
            placeholder="https://seu-erp.com/api"
            value={apiUrl}
            onChange={(e) => setApiUrl(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            URL base da API sem barras no final (ex: https://suaempresa.rspcloud.com.br)
          </p>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="api-key">Token de Autenticação</Label>
          <Input
            id="api-key"
            type="password"
            placeholder="Token obtido após login"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <div className="flex items-center gap-2 text-xs text-blue-600">
            <Info className="h-3 w-3" />
            <span>O token será obtido automaticamente ao fazer login com a API.</span>
          </div>
          {savedToken && (
            <div className="text-xs text-green-600 font-medium mt-1">
              Token já salvo: {savedToken.substring(0, 15)}...
            </div>
          )}
        </div>
        
        <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mt-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Informação sobre autenticação</h3>
          <p className="text-xs text-blue-700">
            O sistema tentará obter o token automaticamente ao fazer login. Para isso, é necessário configurar a URL da API
            corretamente antes de fazer login. Se você já possui um token, pode configurá-lo manualmente nesta página.
          </p>
        </div>
        
        <div className="flex items-center space-x-2 pt-4">
          <Switch 
            id="auto-sync" 
            checked={autoSync}
            onCheckedChange={setAutoSync}
          />
          <Label htmlFor="auto-sync">Sincronização automática</Label>
        </div>
        
        {autoSync && (
          <div className="space-y-2 pl-6">
            <Label htmlFor="sync-interval">Intervalo de sincronização (minutos)</Label>
            <Input
              id="sync-interval"
              type="number"
              min="1"
              placeholder="60"
              value={syncInterval}
              onChange={(e) => setSyncInterval(e.target.value)}
            />
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:justify-between">
        <div className="flex space-x-2 w-full sm:w-auto">
          <Button onClick={handleSaveSettings} className="flex-1 sm:flex-auto">
            Salvar Configurações
          </Button>
          <Button 
            onClick={handleTestConnection} 
            variant="outline" 
            className="flex-1 sm:flex-auto"
            disabled={isTesting}
          >
            {isTesting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Testando...
              </>
            ) : 'Testar Conexão'}
          </Button>
        </div>
        <Button 
          onClick={() => handleSyncData()}
          disabled={isSyncing}
          variant="default"
          className="w-full sm:w-auto"
        >
          {isSyncing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Sincronizando...
            </>
          ) : 'Sincronizar Dados'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ApiSettings;
